import { Component, inject, OnInit, output, WritableSignal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  ButtonComponent,
  CheckboxComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  FormFieldComponent,
  FormFieldHeaderComponent,
  InputComponent,
  MessageComponent,
} from '@akostm/orbit-ui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ConversationType, ConversationTypeTemplateKind } from '@clientwire/api';
import { MessageTemplatesService } from '../../message-templates/message-templates.service';

export interface ConversationTypeFormDialogResult {
  eventType: 'SAVE' | 'CANCEL';
  data?: ConversationType;
}

@Component({
  selector: 'lib-conversation-types-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DialogHeaderComponent,
    DialogFooterComponent,
    ButtonComponent,
    FormFieldComponent,
    InputComponent,
    FormFieldHeaderComponent,
    CheckboxComponent,
    MessageComponent,
  ],
  templateUrl: './conversation-types-dialog.component.html',
  styleUrl: './conversation-types-dialog.component.scss',
})
export class ConversationTypesDialogComponent implements OnInit {
  dialogButtonClicked = output<ConversationTypeFormDialogResult>();

  fb: FormBuilder = inject(FormBuilder);
  sanitizer: DomSanitizer = inject(DomSanitizer);
  dialogRef: DialogRef = inject(DialogRef);
  messageTemplatesService = inject(MessageTemplatesService);
  data: {
    error: WritableSignal<string> | undefined;
    conversationType: ConversationType;
  } = inject(DIALOG_DATA);

  form!: FormGroup;
  headerPreview: SafeHtml = '';
  listPreview: SafeHtml = '';

  error: WritableSignal<string> | undefined;

  readonly saveIcon = faSave;

  // Message template filtering
  readonly emailTemplates = computed(() => {
    const templates = this.messageTemplatesService.messageTemplates();
    if (!templates) return [];
    return templates.filter(template =>
      !template.isSystemTemplate ||
      (template.isSystemTemplate && template.name === 'NotifyParticipantsTemplate')
    );
  });

  readonly smsTemplates = computed(() => {
    const templates = this.messageTemplatesService.messageTemplates();
    if (!templates) return [];
    return templates.filter(template =>
      !template.isSystemTemplate ||
      (template.isSystemTemplate && template.name === 'NotifyParticipantsTemplate')
    );
  });

  readonly locationTemplates = computed(() => {
    const templates = this.messageTemplatesService.messageTemplates();
    if (!templates) return [];
    return templates.filter(template =>
      !template.isSystemTemplate ||
      (template.isSystemTemplate && template.name === 'RequestLocationTemplate')
    );
  });

  ngOnInit() {
    // Load message templates
    this.messageTemplatesService.loadMessageTemplates();

    // Get template names from the conversation type's templates map
    const templates = this.data?.conversationType?.templates || {};
    const emailTemplateName = templates['NOTIFY_PARTICIPANTS_VIA_EMAIL']?.name || '';
    const smsTemplateName = templates['NOTIFY_PARTICIPANTS_VIA_SMS']?.name || '';
    const locationTemplateName = templates['REQUEST_LOCATION']?.name || '';

    this.form = this.fb.group({
      id: [this.data?.conversationType?.id || '', [Validators.required]],
      description: [this.data?.conversationType?.description || ''],
      conversationDataListItemTemplate: [
        this.data?.conversationType?.conversationDataListItemTemplate || '',
        [Validators.required],
      ],
      clientAppHeaderTemplate: [this.data?.conversationType?.clientAppHeaderTemplate || ''],
      headerTemplateData: ['{}'],
      clientAppHeaderTemplateData: ['{}'],
      conversationDataSchema: [
        JSON.stringify(this.data?.conversationType?.conversationDataSchema) || null,
      ],
      sharedReadStatusForUserParticipants: [
        this.data?.conversationType?.sharedReadStatusForUserParticipants || false,
      ],
      displayNameSharedByUsers: [this.data?.conversationType?.displayNameSharedByUsers || null],
      displayNameForWorkflowParticipants: [
        this.data?.conversationType?.displayNameForWorkflowParticipants || null,
      ],
      smsSenderId: [
        this.data?.conversationType?.smsSenderId || null,
        Validators.pattern('^(?=.*[a-zA-Z])[a-zA-Z0-9 ]{1,11}$'),
      ],
      retentionPolicyDays: [
        this.data?.conversationType?.retentionPolicyDays || null,
        [Validators.min(1), Validators.pattern('^[0-9]+$')],
      ],
      // New template selection fields
      emailTemplate: [emailTemplateName],
      smsTemplate: [smsTemplateName],
      locationTemplate: [locationTemplateName],
    });

    this.error = this.data.error;

    this.updateHeaderPreview(true);
    this.updateListPreview(true);

    this.conversationDataListItemTemplate?.valueChanges.subscribe(() => {
      this.updateHeaderPreview(true);
    });

    this.clientAppHeaderTemplate?.valueChanges.subscribe(() => {
      this.updateListPreview(false);
    });
    this.headerTemplateDataForm.valueChanges.subscribe(() => {
      this.updateHeaderPreview(false);
    });
    this.clientAppHeaderTemplateData.valueChanges.subscribe(() => {
      this.updateListPreview(false);
    });
  }

  private updateHeaderPreview(extract: boolean = false) {
    try {
      if (extract) {
        const variables = this.extractTemplateVariables(
          this.conversationDataListItemTemplate.value
        );
        const jsonObject = JSON.parse(variables);
        const jsonValues = JSON.parse(this.headerTemplateDataForm.value);
        if (
          !Object.keys(jsonObject).every((element) => Object.keys(jsonValues).includes(element))
        ) {
          if (variables) {
            this.headerTemplateDataForm.setValue(variables);
          }
        }
      }
      const previewHtml = this.processTemplate(
        this.conversationDataListItemTemplate.value,
        this.headerTemplateDataForm.value
      );
      this.headerPreview = this.sanitizer.bypassSecurityTrustHtml(previewHtml);
    } catch (error) {
      this.headerPreview = this.sanitizer.bypassSecurityTrustHtml(
        '<div class="error">Invalid template</div>'
      );
    }
  }

  private updateListPreview(extract: boolean = false) {
    try {
      if (extract || this.clientAppHeaderTemplateData.value === '{}') {
        const variables = this.extractTemplateVariables(this.clientAppHeaderTemplate.value);
        const jsonObject = JSON.parse(variables);
        const jsonValues = JSON.parse(this.clientAppHeaderTemplateData.value);
        if (
          !Object.keys(jsonObject).every((element) => Object.keys(jsonValues).includes(element))
        ) {
          if (variables) {
            this.clientAppHeaderTemplateData.setValue(variables);
          }
        }
      }
      const previewHtml = this.processTemplate(
        this.clientAppHeaderTemplate.value,
        this.clientAppHeaderTemplateData.value
      );
      this.listPreview = this.sanitizer.bypassSecurityTrustHtml(previewHtml);
    } catch (error) {
      this.listPreview = this.sanitizer.bypassSecurityTrustHtml(
        '<div class="error">Invalid template</div>'
      );
    }
  }

  private processTemplate(template: string, variables: string): string {
    const json = JSON.parse(variables);
    let processed = template;
    Object.entries(json).forEach(([key, value]) => {
      if (value) {
        processed = processed.replaceAll(`{{ ${key} }}`, value as any);
      }
    });
    return processed;
  }

  onSubmit() {
    if (this.form.valid) {
      const formData = { ...this.form.value };

      // Build messageTemplates map from the selected template names
      const messageTemplates: { [key: string]: string } = {};
      if (formData.emailTemplate) {
        messageTemplates[ConversationTypeTemplateKind.NotifyParticipantsViaEmail] = formData.emailTemplate;
      }
      if (formData.smsTemplate) {
        messageTemplates[ConversationTypeTemplateKind.NotifyParticipantsViaSms] = formData.smsTemplate;
      }
      if (formData.locationTemplate) {
        messageTemplates[ConversationTypeTemplateKind.RequestLocation] = formData.locationTemplate;
      }

      // Add messageTemplates to the form data and remove the individual template fields
      formData.messageTemplates = messageTemplates;
      delete formData.emailTemplate;
      delete formData.smsTemplate;
      delete formData.locationTemplate;

      this.dialogButtonClicked.emit({
        eventType: 'SAVE',
        data: formData,
      });
    }
  }

  onCancel() {
    this.dialogButtonClicked.emit({
      eventType: 'CANCEL',
    });
  }

  get id() {
    return this.form.get('id') as FormControl<string>;
  }

  get description() {
    return this.form.get('description') as FormControl<string>;
  }

  get conversationDataListItemTemplate() {
    return this.form.get('conversationDataListItemTemplate') as FormControl<string>;
  }

  get clientAppHeaderTemplate() {
    return this.form.get('clientAppHeaderTemplate') as FormControl<string>;
  }

  get headerTemplateDataForm() {
    return this.form.get('headerTemplateData') as FormControl<string>;
  }

  get clientAppHeaderTemplateData() {
    return this.form.get('clientAppHeaderTemplateData') as FormControl<string>;
  }

  get conversationDataSchema() {
    return this.form.get('conversationDataSchema') as FormControl<string>;
  }

  get sharedReadStatusForUserParticipants() {
    return this.form.get('sharedReadStatusForUserParticipants') as FormControl<boolean>;
  }

  get displayNameSharedByUsers() {
    return this.form.get('displayNameSharedByUsers') as FormControl<string>;
  }

  get displayNameForWorkflowParticipants() {
    return this.form.get('displayNameForWorkflowParticipants') as FormControl<string>;
  }

  get smsSenderId() {
    return this.form.get('smsSenderId') as FormControl<string>;
  }

  get retentionPolicyDays() {
    return this.form.get('retentionPolicyDays') as FormControl<string>;
  }

  get emailTemplate() {
    return this.form.get('emailTemplate') as FormControl<string>;
  }

  get smsTemplate() {
    return this.form.get('smsTemplate') as FormControl<string>;
  }

  get locationTemplate() {
    return this.form.get('locationTemplate') as FormControl<string>;
  }

  extractTemplateVariables(template: string): string {
    if (!template) return '{}';
    const variables = new Set<string>();
    const regex = /{{([^}]+)}}/g;
    let match: RegExpExecArray | null;

    const templateStr = String(template || '');

    while ((match = regex.exec(templateStr)) !== null) {
      if (match[1]) {
        variables.add(match[1].trim());
      }
    }
    const result: Record<string, string> = {};
    variables.forEach((variable) => {
      result[variable] = '';
    });
    return JSON.stringify(result, null, 2);
  }
}
